/**
 * Quick-add from the curated catalogue.
 *
 * Most vendors sell the same few dozen well-known products, and typing each one out
 * produces twenty spellings of "Pedigree Adult Dry Dog Food" that no shopper can
 * search across. Picking from this list LINKS the listing to a master row, so the
 * platform owns the name, brand, pack size and photo — and a correction later
 * reaches every vendor selling it without any of them touching their listing.
 *
 * The vendor still owns the commercial half: which sizes they stock, and at what
 * price. That is the only thing this form asks for, which is the point — one search,
 * one price, done.
 */
import { useState } from 'react';
import { ArrowLeft, Boxes, Check, Loader2, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { form_error_message } from '@/lib/forms';
import { use_catalog_products } from '@/features/catalog-product/hooks';
import type { CatalogProduct } from '@/features/commerce/types';
import {
  EMPTY_VARIANT_DRAFT,
  parse_variant_drafts,
  type CreateVariantInput,
  type VariantDraft,
} from '@/features/product-variant/types';
import { VariantDraftList } from '@/components/variant-draft-list';
import { FeePreviewPanel } from '@/components/fee-preview-panel';
import { use_fee_preview } from '@/features/commission/hooks';
import { AuthImage } from '@/components/auth-image';

const pretty = (s: string) =>
  s
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');

function MasterRow({ product, on_pick }: { product: CatalogProduct; on_pick: () => void }) {
  return (
    <button
      type="button"
      onClick={on_pick}
      className="flex w-full items-center gap-3 border-b-2 border-brand-outline-variant/12 p-3 text-left last:border-0 hover:bg-secondary/40"
      data-testid={`catalog-pick-${product.id}`}
    >
      {product.image_path ? (
        <AuthImage
          path={`${product.image_path}?variant=thumb`}
          alt={product.name}
          className="h-11 w-11 shrink-0 overflow-hidden rounded-input object-cover"
        />
      ) : (
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-input bg-secondary text-muted-foreground">
          <Boxes className="h-5 w-5" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{product.name}</p>
        <p className="truncate text-xs text-muted-foreground">
          {[product.brand, pretty(product.category), product.pack_size].filter(Boolean).join(' · ')}
        </p>
      </div>
      <span className="text-xs font-semibold text-primary">Add</span>
    </button>
  );
}

export function CatalogProductPicker({
  vertical,
  on_link,
  on_close,
  linking,
}: {
  vertical: 'FOOD' | 'PHARMACY';
  /** Create the listing. The caller owns the mutation, so each page keeps its own cache. */
  on_link: (catalog_product_id: string, variants: CreateVariantInput[]) => Promise<void>;
  on_close: () => void;
  linking: boolean;
}) {
  const [text, set_text] = useState('');
  const [query, set_query] = useState('');
  const [picked, set_picked] = useState<CatalogProduct | null>(null);
  const [variants, set_variants] = useState<VariantDraft[]>([{ ...EMPTY_VARIANT_DRAFT }]);
  const [error, set_error] = useState<string | null>(null);

  const list_q = use_catalog_products({ vertical, page_size: 20, ...(query && { q: query }) }, picked === null);
  /*
   * The chosen master fixes the category, so the fee is known before the vendor types
   * a price — which is the whole reason to show it here. Competing on price against
   * other vendors selling the SAME product is the point of the catalogue, and that
   * comparison happens at the customer-facing number, not the vendor's.
   */
  const fee_q = use_fee_preview(vertical, picked?.category);
  const items = list_q.data?.items ?? [];

  function pick(product: CatalogProduct) {
    set_picked(product);
    set_error(null);
    // Pre-fill the first variant label with the master's pack size — for most
    // products that IS the only size, so the vendor only has to type a price.
    set_variants([{ ...EMPTY_VARIANT_DRAFT, label: product.pack_size ?? '' }]);
  }

  async function submit() {
    if (!picked) return;
    set_error(null);
    const parsed = parse_variant_drafts(variants);
    if ('error' in parsed) return set_error(parsed.error);
    try {
      await on_link(picked.id, parsed.variants);
      on_close();
    } catch (err) {
      set_error(form_error_message(err, 'Could not add this product.'));
    }
  }

  return (
    <Card data-testid="catalog-picker">
      <div className="flex items-center justify-between border-b-2 border-brand-outline-variant/15 px-5 py-3.5">
        <h2 className="font-heading text-base font-bold">
          {picked ? 'Set your prices' : 'Add from catalogue'}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={picked ? () => set_picked(null) : on_close}
          data-testid="catalog-picker-back"
        >
          {picked ? (
            <>
              <ArrowLeft className="h-4 w-4" /> Back
            </>
          ) : (
            'Close'
          )}
        </Button>
      </div>

      <CardContent className="flex flex-col gap-4 p-5">
        {picked === null ? (
          <>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                set_query(text.trim());
              }}
            >
              <Input
                value={text}
                onChange={(e) => set_text(e.target.value)}
                placeholder="Search by name or brand"
                data-testid="catalog-picker-search"
              />
              <Button type="submit" variant="outline" data-testid="catalog-picker-search-go">
                <Search className="h-4 w-4" />
              </Button>
            </form>

            {list_q.isLoading && !list_q.data ? (
              <div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : list_q.error ? (
              <ErrorState
                data-testid="catalog-picker-error"
                title="Couldn't load the catalogue"
                description={form_error_message(list_q.error, 'Please try again.')}
              />
            ) : items.length === 0 ? (
              <EmptyState
                data-testid="catalog-picker-empty"
                icon={Boxes}
                title={query ? 'Nothing matches that' : 'The catalogue is empty'}
                description={
                  query
                    ? 'Try a different name, or add the product yourself.'
                    : 'Add your product manually for now — common products will appear here.'
                }
              />
            ) : (
              <ul className="-mx-2 overflow-hidden rounded-card border-2 border-brand-outline-variant/15" data-testid="catalog-picker-list">
                {items.map((p) => (
                  <li key={p.id}>
                    <MasterRow product={p} on_pick={() => pick(p)} />
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            {/*
             * Read-only on purpose. These fields belong to the master, so the vendor
             * seeing them greyed out is the honest signal that corrections come from
             * us rather than from them — and it is why the same product is spelled
             * the same way across every shop.
             */}
            <div
              className="flex items-start gap-3 rounded-card border-2 border-brand-outline-variant/15 bg-secondary/30 p-3"
              data-testid="catalog-picker-chosen"
            >
              {picked.image_path ? (
                <AuthImage
                  path={`${picked.image_path}?variant=thumb`}
                  alt={picked.name}
                  className="h-12 w-12 shrink-0 overflow-hidden rounded-input object-cover"
                />
              ) : (
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-input bg-secondary text-muted-foreground">
                  <Boxes className="h-5 w-5" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold" data-testid="catalog-picker-chosen-name">
                  {picked.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {[picked.brand, pretty(picked.category), picked.pack_size].filter(Boolean).join(' · ')}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Name, brand and photo are kept up to date by Pet Market. You set the sizes and prices.
                </p>
              </div>
            </div>

            {picked.is_prescription_required && (
              <p className="text-sm font-semibold text-destructive">
                Prescription required — customers must upload one before ordering.
              </p>
            )}

            <VariantDraftList value={variants} onChange={set_variants} />

            <FeePreviewPanel fee={fee_q.data} variants={variants} />

            {error && (
              <p className="text-sm text-destructive" role="alert" data-testid="catalog-picker-error-message">
                {error}
              </p>
            )}

            <div className="flex gap-2">
              <Button onClick={submit} variant="gradient" disabled={linking} data-testid="catalog-picker-save">
                {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Add to my shop
              </Button>
              <Button variant="ghost" onClick={() => set_picked(null)}>
                Choose a different product
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
